import {useCallback, useMemo} from 'react';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {makeContext} from '../MakeContext';
import {useSearchableJSONTreePropsContext} from './SearchableJSONTreeProvider';
import {useSearchableJSONTreeSettingsContext} from './SearchableJSONTreeSettings';
import {filterJSON, JSONNode} from './util/filterJSON';
import {unflattenJSON} from './util/unflattenJSON';

export const useFilteredJSON = () => {
  const {
    json = {},
    filterOptions: {
      stringSearchFunction: propsStringSearchFunction,
      nodeTransformFunction: propsNodeTransformFunction,
      excludeList: propsExcludeList,
    } = {},
  } = useSearchableJSONTreePropsContext();
  const {isSearching, getIsSearchMatch} = usePanelSearchDebouncedContext();

  // The default node transform function is unflattenJSON.
  // If you pass in a custom node transform function, it will run *in addition to* this default node transform function.
  const {unflattenDelimiter} = useSearchableJSONTreeSettingsContext();
  const nodeTransformFunction = useCallback(
    (node: JSONNode) => {
      const propsTransformedNode =
        propsNodeTransformFunction == null
          ? node
          : propsNodeTransformFunction(node);

      return unflattenJSON({
        node: propsTransformedNode,
        delimiter: unflattenDelimiter,
      });
    },
    [propsNodeTransformFunction, unflattenDelimiter]
  );

  // The default search function is getIsSearchMatch from SearchDebouncedContext.
  // If you pass in a custom search function, it will run *instead of* this default search function.
  const stringSearchFunction = useCallback(
    (str: string) => {
      if (!isSearching) {
        return true;
      }
      return propsStringSearchFunction?.(str) ?? getIsSearchMatch(str);
    },
    [getIsSearchMatch, isSearching, propsStringSearchFunction]
  );

  return useMemo(
    () =>
      filterJSON({
        node: json,
        options: {
          excludeList: propsExcludeList,
          stringSearchFunction,
          nodeTransformFunction,
        },
      }),
    [json, nodeTransformFunction, propsExcludeList, stringSearchFunction]
  );
};

export const {Provider: FilteredJSONProvider, hook: useFilteredJSONContext} =
  makeContext({
    contextType: 'FilteredJSON',
    getContextValue: useFilteredJSON,
  });
